import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import iconNotify from "../assets/icon/general/notifiche.png";
import iconId from "../assets/icon/arrow/freccia.png";
import api , { getWsUrl }from "../Api";
import ACTIVITY_OPTIONS from "./utils/ActivityOptions";



export default function NotificationCenter() {
  const [events, setEvents] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const getCurrentMonth = () => {
    const raw = new Date().toLocaleDateString("it-IT", {
      month: "long",
      year: "numeric",
    });
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const getActivityLabel = (value) => {
    const option = ACTIVITY_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const fetchLastNotifications = async () => {
    try {
      let endpoint = "/notifications/recent?limit=5";
      if (user && user.role === "rup") {
        endpoint = "/notifications/rup?limit=5";
      }
      const res = await api.get(endpoint);
      const limited = res.data.slice(0, 4);
      setEvents(limited);
    } catch (err) {
      console.error("Errore nel recupero notifiche:", err);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get("/notifications/unread-count");
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      console.error("Errore nel conteggio notifiche non lette:", err);
    }
  };

  const markNotificationsAsRead = async () => {
    try {
      await api.patch("/notifications/mark-read");
      setUnreadCount(0);
    } catch (err) {
      console.error("Errore aggiornamento lettura notifiche:", err);
    }
  };

  useEffect(() => {
    if (user) {
      fetchLastNotifications();
      fetchUnreadCount();
      markNotificationsAsRead();

      const socket = new WebSocket(getWsUrl());

      socket.onopen = () => console.log("🟢 WebSocket connesso");

      socket.onmessage = async (event) => {
        const data = JSON.parse(event.data);

        let assignedToName = data.assignedTo;
        if (!isNaN(assignedToName)) {
          try {
            const res = await api.get(`/user/${assignedToName}`);
            const user = res.data;
            assignedToName = `${user.firstName} ${user.lastName}`;
          } catch (err) {
            console.warn("⚠️ Errore nel fetch del nome utente:", err);
          }
        }

        const enrichedData = {
          ...data,
          assignedTo: assignedToName,
          is_read: false,
        };

        setEvents((prev) => {
          const updated = [enrichedData, ...prev];
          const unique = updated.filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.id === item.id && t.time === item.time)
          );
          return unique.slice(0, 4);
        });

        fetchUnreadCount();
      };

      socket.onclose = () => console.log("🔴 WebSocket disconnesso");

      return () => socket.close();
    }
  }, [user]);

  const formatDateWithWeekday = (dateStr) => {
    const [day, month, year] = dateStr.split("/").map(Number);
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString("it-IT", { weekday: "short" });
    const dayOfMonth = String(date.getDate()).padStart(2, "0");
    return `${capitalizeFirst(weekday)}, ${dayOfMonth}`;
  };

  const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const goToActivity = (activityId) => {
    navigate(`/activity-details/${activityId}`);
  };

  const goToAllNotifications = () => {
    navigate("/all-activity");
  };

  return (
    <div className="w-full h-full bg-primary rounded-2xl shadow p-4 text-white border-2 border-secondary flex flex-col">
      <div className="flex justify-between items-center mb-2 relative">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Centro notifiche</h2>
          <button
            onClick={goToAllNotifications}
            className="text-sm text-green-400 underline hover:text-green-600 transition"
          >
            Vedi tutte
          </button>
        </div>
        <div className="relative">
          <img
            src={iconNotify}
            alt="Icona notifiche"
            className="w-8 h-8 border rounded-full p-1"
          />
          {unreadCount > 0 && (
            <div className="absolute -top-1.5 -right-1.5 bg-yellow-600 text-primary text-[13px] font-bold w-6 h-6 flex items-center justify-center rounded-full border-2 border-primary">
              {unreadCount}
            </div>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-300 mb-12">{getCurrentMonth()}</p>

      <ul className="space-y-4 overflow-y-auto pr-1 flex-1">
        {events.length === 0 ? (
          <li className="text-sm text-gray-400">Nessuna notifica recente</li>
        ) : (
          events.map((event) => {
            const activityList = event.message
              ? event.message.split(",").map((val) => val.trim())
              : [];
            const firstActivity = activityList[0]
              ? getActivityLabel(activityList[0])
              : "Attività sconosciuta";
            const extraCount = activityList.length - 1;

            return (
              <li
                key={`${event.id}-${event.time}`}
                className="flex items-center justify-between gap-4 border-b border-white/10 pb-4 text-sm cursor-pointer hover:bg-white/5 transition"
                onClick={() => goToActivity(event.activity_id)}
              >
                <div className="flex gap-4">
                  <div className="flex flex-col items-start text-xs text-gray-300 min-w-[70px]">
                    <span className="font-mono">Assegnato:</span>
                    <span>{formatDateWithWeekday(event.date)}</span>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="bg-secondary px-2 py-0.5 rounded-full text-xs text-white">
                        {firstActivity}
                        {extraCount > 0 && ` +${extraCount}`}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">
                      Assegnata a: {event.assignedTo}
                    </span>
                  </div>
                </div>
                <img
                  src={iconId}
                  alt="Vai all'attività"
                  className="w-5 h-5 opacity-70 hover:opacity-100 transition"
                />
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
